import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import BrowsePage from "@/pages/BrowsePage";
import AuctionDetailPage from "@/pages/AuctionDetailPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import SellerDashboard from "@/pages/SellerDashboard";
import SellPage from "@/pages/SellPage";
import AdminPage from "@/pages/AdminPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import BoostSuccessPage from "@/pages/BoostSuccessPage";
import AuthCallback from "@/pages/AuthCallback";
import HowItWorksPage from "@/pages/HowItWorksPage";

function ProtectedRoute({ children, requireAdmin = false, requireSeller = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="max-w-7xl mx-auto p-16 text-zinc-500 uppercase tracking-widest">Loading…</div>;
  }
  if (!user || typeof user !== "object") {
    return <Navigate to="/login" replace />;
  }
  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  if (requireSeller && !user.is_seller) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppShell() {
  const location = useLocation();
  // Handle Emergent Google auth callback synchronously (session_id in URL fragment)
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const hideChrome = ["/login", "/register"].includes(location.pathname);
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {!hideChrome && <Navbar />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/browse" element={<BrowsePage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/auctions/:id" element={<AuctionDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
          />
          <Route
            path="/dashboard/selling"
            element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
          />
          <Route
            path="/seller"
            element={<ProtectedRoute requireSeller><SellerDashboard /></ProtectedRoute>}
          />
          <Route
            path="/sell"
            element={<ProtectedRoute><SellPage /></ProtectedRoute>}
          />
          <Route
            path="/admin"
            element={<ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>}
          />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/boost-success" element={<BoostSuccessPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
        <Toaster position="bottom-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
